
import React, {
  createContext, JSX, ReactNode, useCallback,
  useContext, useEffect, useMemo, useState
} from 'react'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { MyAccount, MyAccountUsage, Plan, PlanFeatureKey, PlanLimitKey } from 'types/api'
import getMyAccountQuery from 'graphql/getMyAccount.graphql'

export type EntitlementsStatus = 'loading' | 'ready' | 'error'

const STRIPE_RAIL = 'stripe'
const SHOPIFY_RAIL = 'shopify'

export interface EntitlementsContextType {
  status: EntitlementsStatus
  error: string | null
  account: MyAccount | null
  tier: string | null
  superuser: boolean
  staffUncapped: boolean
  suspended: boolean
  acquisitionSource: string
  shopifyBilled: boolean
  limitFor: (key: PlanLimitKey) => number | null
  enforcedLimitFor: (key: PlanLimitKey) => number | null
  featureFor: (key: PlanFeatureKey) => boolean
  plans: Plan[]
  planFor: (tier: string) => Plan | null
  upgradeForFeature: (key: PlanFeatureKey) => Plan | null
  upgradeForLimit: (key: PlanLimitKey, currentCap: number | null) => Plan | null
  usage: MyAccountUsage
  refetch: () => Promise<void>
}

const defaultContext: EntitlementsContextType = {
  status: 'loading',
  error: null,
  account: null,
  tier: null,
  superuser: false,
  staffUncapped: false,
  suspended: false,
  acquisitionSource: STRIPE_RAIL,
  shopifyBilled: false,
  limitFor: () => null,
  enforcedLimitFor: () => null,
  featureFor: () => false,
  plans: [],
  planFor: () => null,
  upgradeForFeature: () => null,
  upgradeForLimit: () => null,
  usage: { events: 0, chatOverlays: 0, storageBytes: 0 },
  refetch: async () => {}
}

const PlanContext = createContext<EntitlementsContextType>(defaultContext)
PlanContext.displayName = 'Plan Context'

export const PlanProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { auth0Token, streamToolsUser } = useAuth()
  const [account, setAccount] = useState<MyAccount | null>(null)
  const [status, setStatus] = useState<EntitlementsStatus>('loading')
  const [error, setError] = useState<string | null>(null)

  const superuser = streamToolsUser?.superuser === true
  const staffUncapped = superuser || streamToolsUser?.admin === true

  const suspendedAt = account?.suspendedAt ?? null
  const suspended = suspendedAt !== null && suspendedAt !== ''

  const acquisitionSource = account?.acquisitionSource ?? STRIPE_RAIL
  const shopifyBilled = acquisitionSource === SHOPIFY_RAIL

  const refetch = useCallback(async () => {
    if (auth0Token === undefined) return
    setError(null)
    setStatus((prev) => prev === 'ready' ? 'ready' : 'loading')
    try {
      const { account: fetched } = await graphqlRequest<{ account: MyAccount }>(
        getMyAccountQuery, {}, auth0Token
      )
      setAccount(fetched)
      setStatus('ready')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your plan')
      setStatus((prev) => prev === 'ready' ? 'ready' : 'error')
    }
  }, [auth0Token])

  useEffect(() => { void refetch() }, [refetch])

  const plans = useMemo<Plan[]>(() => account?.plans ?? [], [account])

  const planFor = useCallback((tier: string): Plan | null =>
    plans.find((p) => p.tier === tier) ?? null
  , [plans])

  const limitFor = useCallback((key: PlanLimitKey): number | null => {
    if (staffUncapped) return null
    return account?.limits[key] ?? null
  }, [staffUncapped, account])

  const enforcedLimitFor = useCallback((key: PlanLimitKey): number | null => {
    if (staffUncapped) return null
    if (status !== 'ready') return null
    if (account === null || !account.limitsEnforced) return null
    return account.limits[key]
  }, [staffUncapped, status, account])

  const featureFor = useCallback((key: PlanFeatureKey): boolean => {
    if (staffUncapped) return true
    if (account !== null && !account.limitsEnforced) return true
    return account?.limits[key] === true
  }, [staffUncapped, account])

  const upgradeForFeature = useCallback((key: PlanFeatureKey): Plan | null =>
    plans.find((p) => p.purchasable && p.limits[key]) ?? null
  , [plans])

  const upgradeForLimit = useCallback((key: PlanLimitKey, currentCap: number | null): Plan | null => {
    if (currentCap === null) return null
    return plans.find((p) => {
      if (!p.purchasable) return false
      const cap = p.limits[key]
      return cap === null || cap > currentCap
    }) ?? null
  }, [plans])

  const usage = useMemo<MyAccountUsage>(
    () => account?.usage ?? { events: 0, chatOverlays: 0, storageBytes: 0 },
    [account]
  )

  const value = useMemo<EntitlementsContextType>(() => ({
    status,
    error,
    account,
    tier: account?.tier ?? null,
    superuser,
    staffUncapped,
    suspended,
    acquisitionSource,
    shopifyBilled,
    limitFor,
    enforcedLimitFor,
    featureFor,
    plans,
    planFor,
    upgradeForFeature,
    upgradeForLimit,
    usage,
    refetch
  }), [
    status, error, account, superuser, staffUncapped, suspended,
    acquisitionSource, shopifyBilled,
    limitFor, enforcedLimitFor, featureFor, plans, planFor,
    upgradeForFeature, upgradeForLimit, usage, refetch
  ])

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  )
}

export const useEntitlements = (): EntitlementsContextType =>
  useContext(PlanContext)

export const tierLabel = (tier: string): string =>
  tier.length === 0 ? tier : tier.charAt(0).toUpperCase() + tier.slice(1)

export const planDisplayName = (plan: Pick<Plan, 'tier' | 'label'>): string =>
  plan.label ?? tierLabel(plan.tier)
